import { Box, ButtonGroup, Tooltip, Zoom } from "@material-ui/core";
import { ThumbDownAlt, ThumbUpAlt } from "@material-ui/icons";
import { useAccessControl, useAuth, useVerifyPermission } from "@airborne/react-admin";
import * as React from "react";
import { FC, Fragment } from "react";
import { Datagrid, ListProps, TextField, useNotify, useRefresh } from "react-admin";
import { DetailButton } from "../../../Components/DetailButton";
import { ConfirmButton } from "../../../Components/ConfirmButton";
import { CustomDateField } from "../../../Components/CustomDateField";
import FilterToolbar from "../../../Components/FiltersToolbar/FilterToolbar";
import CreatedByField from "../../../Components/ReferenceFields/CreatedBy/CreatedByField";
import { DefaultCreatedDateSort, DefaultPerPageSize } from "../../../Constants/Constant";
import { useAppDataProvider } from "../../../dataprovider";
import { FilterInputType } from "../../../Enums/FilterTypes";
import { RequestStatus } from "../../../Enums/RequestStatus";
import { ExcelExport } from "../../../Helper/ExportExcel";
import { TabbedDatagridProps } from "../../../Models/TabbedDataGridProps";
import { ManuelCoverageControlListTitle, SourceRequestOrder } from "./Constant/Source";
import PoolList from "../../../Components/PoolListComponent/PoolListComponent";

const TabbedDatagrid: FC<TabbedDatagridProps> = props => {
  const dataProvider = useAppDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { user } = useAuth();
  const { canCreate, canEdit, canRead, hasAccess } = useAccessControl(SourceRequestOrder);

  const acceptButtonClick = record => e => {
    record.requestStatus = RequestStatus.Pool;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };

  const rejectButtonClick = record => e => {
    record.requestStatus = RequestStatus.NotPool;
    dataProvider.updateRequestOrder(record).then(result => {
      notify(`Record updated`, "success", false, true);
      refresh();
    });
  };

  const AcceptReceivingButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#12824C",
        }}
        size="medium"
        click={acceptButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Accept">
          <ThumbUpAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };

  const RejectReceivingButton = ({ record }) => {
    return (
      <ConfirmButton
        {...props}
        variant="text"
        style={{
          color: "#primary",
        }}
        size="medium"
        click={rejectButtonClick(record)}
      >
        <Tooltip TransitionComponent={Zoom} title="Reject">
          <ThumbDownAlt />
        </Tooltip>
      </ConfirmButton>
    );
  };
  const DetailsButton = ({ record }) => {
    return <DetailButton record={record} resource={"requestorders"} />;
  };
  const OptButtonGroup = ({ record }) => {
    return (
      <Box style={{ flex: 1, flexDirection: "row" }}>
        <ButtonGroup variant="text" size="small">
          <DetailsButton record={record} />
          {user?.isCustomer === false && canCreate && (
            <>
              <AcceptReceivingButton record={record} />
              <RejectReceivingButton record={record} />{" "}
            </>
          )}
        </ButtonGroup>
      </Box>
    );
  };

  return (
    <Fragment>
      <Datagrid {...props} optimized basePath={SourceRequestOrder}>
        <OptButtonGroup record={props} />
        <TextField source="refNo" label="Ref No" />
        <TextField source="componentDescription" label="Description" />
        <CreatedByField label="Created By" link={false} />
        <CustomDateField source="createdDate" label="Created Date" showTime />
      </Datagrid>
    </Fragment>
  );
};

const ManuelCoverageControlList: FC<ListProps> = props => {
  useVerifyPermission("customroutes", "manuelcoveragecontrol.read");
  const ExportToExcel = data => {
    ExcelExport(
      [
        {
          header: "Ref No",
          key: "Ref No",
        },
        {
          header: "Description",
          key: "Description",
        },
        {
          header: "Created By",
          key: "Created By",
        },
        {
          header: "Created Date",
          key: "Created Date",
        },
      ],
      data.map(v => [v.refNo, v.componentDescription, v.createdBy, v.createdDate]),
      "Manuel Coverage Control"
    );
  };
  return (
    <PoolList
      {...props}
      hasCreate={false}
      bulkActionButtons={false}
      resource={SourceRequestOrder}
      basePath={SourceRequestOrder}
      title={ManuelCoverageControlListTitle}
      filterDefaultValues={{
        requestStatus__eq: RequestStatus.ReturnManuelCoverageControl,
      }}
      sort={DefaultCreatedDateSort}
      perPage={DefaultPerPageSize}
      exporter={ExportToExcel}
      filters={
        <FilterToolbar
          dataSource={[
            {
              source: "refNo",
              inputType: FilterInputType.Text,
              label: "Ref No",
            },
            {
              source: "componentDescription",
              inputType: FilterInputType.Text,
              label: "Customer Description",
            },
            {
              source: "createdDate",
              inputType: FilterInputType.Date,
              label: "Created Date",
            },
          ]}
        />
      }
    >
      <TabbedDatagrid />
    </PoolList>
  );
};

export default ManuelCoverageControlList;
